import React, { useCallback, useMemo } from "react";
import { Row, Col, Button, ListGroup } from "react-bootstrap";
import { PacmanLoader } from "react-spinners";
import { createColumnHelper } from "@tanstack/react-table";
import { GenericTable } from "components/GenericTable";
import { createElementIdFactory, createReusableTableElementIds } from "utils/elementIds";
import { PAGE_SIZES } from "utils/pageSizeConfig";
import "containers/common/List.css";
import "containers/common/Table.css";

function formatDate(value) {
  if (!value) {
    return "";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
}

function getStatusText(integration) {
  if (integration?.is_accepted === null || integration?.is_accepted === undefined) {
    return "Pending";
  }
  if (integration?.is_accepted === true && integration?.accept_reject_by === "system") {
    return "Auto-Accepted";
  }
  if (integration?.is_accepted === true) {
    return "Accepted";
  }
  return "Rejected";
}

export default function SLMPropertyIntegrationsList({
  filter,
  data = [],
  isLoading = false,
  isAuthenticated,
  controlType = "list",
  saveRecord,
  updateTableState,
  acceptIntegration,
  rejectIntegration,
  validateRelatedRecord,
  context,
  ctx,
}) {
  const rows = useMemo(() => (Array.isArray(data) ? data : []), [data]);
  const createId = useMemo(() => createElementIdFactory("slm-property-integrations-list"), []);
  const tableElementIds = useMemo(() => createReusableTableElementIds(createId, "main"), [createId]);
  const columnHelper = useMemo(() => createColumnHelper(), []);

  const normalizeValidationResult = useCallback((result, fallbackMessage) => {
    if (Array.isArray(result)) {
      const [isValid = true, validationMessage = ""] = result;
      return { isValid: Boolean(isValid), message: validationMessage || fallbackMessage || "Invalid record." };
    }

    if (result && typeof result === "object") {
      const isValid = result.isValid !== undefined ? Boolean(result.isValid) : true;
      const message = result.validationMessage || result.message || fallbackMessage || "Invalid record.";
      return { isValid, message };
    }

    if (typeof result === "boolean") {
      return { isValid: result, message: fallbackMessage || "Invalid record." };
    }

    return { isValid: true, message: "" };
  }, []);

  const runRelatedValidation = useCallback((record) => {
    if (typeof validateRelatedRecord !== "function") {
      return { isValid: true, message: "" };
    }

    return normalizeValidationResult(validateRelatedRecord(record, "slmPropertyIntegrations"), "Property integration validation failed.");
  }, [normalizeValidationResult, validateRelatedRecord]);

  const resolveContext = useCallback(() => {
    const declaredContext = (context || ctx || "").toLowerCase();
    if (declaredContext === "slmlease" || declaredContext === "slmsale") {
      return declaredContext;
    }

    if (filter?.leaseid || filter?.lease_id) {
      return "slmlease";
    }

    if (filter?.saleid || filter?.sale_id) {
      return "slmsale";
    }

    const pathname = window?.location?.pathname?.toLowerCase() || "";
    if (pathname.includes("/slmleases/")) {
      return "slmlease";
    }

    if (pathname.includes("/slmsales/")) {
      return "slmsale";
    }

    return "";
  }, [context, ctx, filter]);

  const getRelatedIdPayload = useCallback((row) => {
    const activeContext = resolveContext();
    if (activeContext === "slmsale") {
      return { sale_id: row.sale_id ?? filter?.saleid ?? filter?.sale_id ?? null };
    }

    return { lease_id: row.lease_id ?? filter?.leaseid ?? filter?.lease_id ?? null };
  }, [filter, resolveContext]);

  const handleDelete = useCallback(async (row) => {
    const validation = runRelatedValidation(row);
    if (!validation.isValid) {
      window.alert(validation.message);
      return;
    }

    const confirmed = window.confirm("Are you sure you want to delete this integration?");
    if (!confirmed) {
      return;
    }

    // The API has no DELETE route for integrations; archiving matches the bulk "Delete All" behaviour.
    const archivedRow = {
      ...row,
      ...getRelatedIdPayload(row),
      archived_date: new Date().toISOString(),
    };

    try {
      await saveRecord?.(archivedRow, "slmPropertyIntegrations", "property_integration_id");
      updateTableState?.(
        rows.filter((item) => item.property_integration_id !== row.property_integration_id),
        "integrations"
      );
    } catch (error) {
      window.alert(error + ". Please refresh the page (F5) to try again.");
    }
  }, [getRelatedIdPayload, rows, runRelatedValidation, saveRecord, updateTableState]);

  const getStatusButton = useCallback((row) => {
    const handleAccept = (event) => {
      const validation = runRelatedValidation(row.original);
      if (!validation.isValid) {
        window.alert(validation.message);
        return;
      }

      acceptIntegration?.(row, event);
    };

    const handleReject = (event) => {
      const validation = runRelatedValidation(row.original);
      if (!validation.isValid) {
        window.alert(validation.message);
        return;
      }

      rejectIntegration?.(row, event);
    };

    if (row.original.is_accepted === null || row.original.is_accepted === undefined) {
      return (
        <Button type="button" size="sm" variant="secondary" onClick={handleAccept}>
          Pending
        </Button>
      );
    }

    if (row.original.is_accepted === true) {
      return (
        <Button
          type="button"
          size="sm"
          variant="success"
          onClick={handleReject}
        >
          {row.original.accept_reject_by === "system" ? "Auto-Accepted" : "Accepted"}
        </Button>
      );
    }

    return (
      <Button type="button" size="sm" variant="danger" onClick={handleAccept}>
        Rejected
      </Button>
    );
  }, [acceptIntegration, rejectIntegration, runRelatedValidation]);

  const columns = useMemo(
    () => [
      columnHelper.accessor("property_integration_id", { id: "property_integration_id", header: "ID", size: 70 }),
      columnHelper.accessor("value_to_match", { id: "value_to_match", header: "CoLin Address", size: 220, meta: { wrap: true } }),
      columnHelper.accessor("integration_property_id", { id: "integration_property_id", header: "CoreLogic Property ID", size: 100 }),
      columnHelper.accessor("integration_address", {
        id: "integration_address",
        header: "CoreLogic Address",
        size: 240,
        meta: { wrap: true },
        cell: (info) => {
          const propertyId = info.row.original.integration_property_id;
          const address = info.getValue();
          return propertyId ? (
            <a href={`https://rpp.corelogic.com.au/property/${propertyId}`} target="_blank" rel="noreferrer">
              {address}
            </a>
          ) : (
            address
          );
        },
      }),
      columnHelper.accessor("integration_parcel_identifier", { id: "integration_parcel_identifier", header: "Parcel", size: 110 }),
      columnHelper.accessor("match_method", { id: "match_method", header: "Matched By", size: 110 }),
      columnHelper.accessor("parse_status", { id: "parse_status", header: "Parsing Status", size: 120 }),
      columnHelper.accessor("match_type", { id: "match_type", header: "Match Type", size: 100 }),
      columnHelper.accessor("match_rule", { id: "match_rule", header: "Match Rule", size: 120 }),
      columnHelper.accessor("created_date", { id: "created_date", header: "Date Created", cell: (info) => formatDate(info.getValue()), size: 110 }),
      columnHelper.accessor("last_edited_date", { id: "last_edited_date", header: "Last Edited", cell: (info) => formatDate(info.getValue()), size: 110 }),
      columnHelper.accessor("last_edited_by", { id: "last_edited_by", header: "Edited By", size: 110 }),
      columnHelper.display({ id: "status_action", header: "Status", size: 130, cell: (info) => getStatusButton(info.row) }),
      columnHelper.display({
        id: "delete_action",
        header: "",
        size: 90,
        cell: (info) => (
          <Button type="button" size="sm" variant="danger" onClick={() => handleDelete(info.row.original)}>
            Delete
          </Button>
        ),
      }),
    ],
    [columnHelper, getStatusButton, handleDelete]
  );

  const renderList = () => (
    <ListGroup>
      {rows.map((row, index) => (
        <ListGroup.Item key={row.property_integration_id ?? `integration-${index}`}>
          <a href={`https://rpp.corelogic.com.au/property/${row.integration_property_id}`} target="_blank" rel="noreferrer">
            {row.integration_property_id}: {row.integration_address}
          </a>{" "}
          ({getStatusText(row).toLowerCase()})
        </ListGroup.Item>
      ))}
    </ListGroup>
  );

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="SLMPropertyIntegrationsSection" id={createId("container")}>
      <Row className="show-Container g-2">
        <Col xs={12}>
          {isLoading ? (
            <div className="outer-div">
              <div className="sweet-loading inner-div">
                <PacmanLoader cssOverride={{ display: "block", margin: "0 auto" }} size={20} color="#e03c31" loading={isLoading} />
              </div>
            </div>
          ) : controlType === "table" ? (
            <GenericTable
              columns={columns}
              data={rows}
              pageSize={PAGE_SIZES.slmPropertyIntegrationList}
              enablePagination={true}
              hasHeaderSorting={true}
              section="slm-property-integrations-list"
              elementIds={tableElementIds}
              containerClassName="componentRendering"
              enforceMinRowsHeight={true}
            />
          ) : (
            renderList()
          )}
        </Col>
      </Row>
    </div>
  );
}
